import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  type ReactNode,
  type Ref,
} from 'react';
import { styled } from '@mui/material/styles';

import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { PAPER_TEXTURE } from '../../shared/theme/texture';
import { commits, startsOnOwnDrag, useSwipeGesture } from '../../shared/useSwipeGesture';

/**
 * Search and Filter below 900px: a folder tab on the left edge that pulls the
 * filters open over the events.
 *
 * Closed, only the tab shows. Opening slides the panel in from the left while a
 * warm scrim fades up over everything else, both on the same curve and done at
 * the same moment, so the list stays in view behind the filters and each change
 * can be seen landing in it. The tab, its ✕ and the scrim all close it again.
 *
 * On a touch screen the panel also follows a finger: a swipe right on the events
 * list pulls it open (App forwards that drag here), and a swipe left anywhere on
 * the open panel or the scrim pushes it closed. Tapping the tab animates the whole
 * way, accelerating out and decelerating in; a release after a drag finishes from
 * wherever the finger left it.
 *
 * The panel moves by transform and never re-renders to do it, so a drag costs a
 * style write per frame rather than a render of every filter.
 */

/** The tab's width on screen. It overlaps the panel by 1px, so no seam can open between them. */
const TAB_W = 35;
/** Tall enough for "Search + Filter", plus an S-curve into the edge at each end. */
const TAB_H = 173;
const CURVE_H = 28;
/** The tab's middle, as a share of the screen's height. */
const TAB_AT = '77.5%';
const PANEL_MAX = 500;

const OPEN_MS = 420;
/** Accelerates out, decelerates in. */
const EASE_IN_OUT = 'cubic-bezier(0.65, 0, 0.35, 1)';
/** After a drag, which is already moving: decelerate only. */
const EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)';

/** #595047 at 77%. */
const SCRIM = 'rgba(89, 80, 71, 0.77)';

const Scrim = styled('div')({
  position: 'fixed',
  inset: 0,
  zIndex: 1149,
  background: SCRIM,
  opacity: 0,
  visibility: 'hidden',
  pointerEvents: 'none',
  touchAction: 'none',
});

const Panel = styled('div')({
  position: 'fixed',
  top: 0,
  bottom: 0,
  left: 0,
  zIndex: 1150,
  width: `min(${PANEL_MAX}px, calc(100vw - ${TAB_W + 1}px))`,
  ...PAPER_TEXTURE,
  transform: 'translateX(-100%)',
  willChange: 'transform',
  touchAction: 'pan-y',
});

const Contents = styled('div')({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  outline: 'none',
});

const Bar = styled('div')({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 12,
  padding: 'calc(env(safe-area-inset-top, 0px) + 24px) 24px 16px 16px',
});

const Title = styled('h2')({
  margin: 0,
  fontFamily: FONTS.display,
  fontSize: 19,
  fontWeight: 700,
  letterSpacing: '-0.01em',
  whiteSpace: 'nowrap',
  color: BRAND.parchment,
});

const Count = styled('span')({
  fontFamily: FONTS.body,
  fontSize: 11.5,
  color: 'rgba(243,238,231,0.85)',
  whiteSpace: 'nowrap',
});

const Body = styled('div')({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  // Its foot keeps Submit Feedback! reachable above a phone's toggle band.
  padding: '0 16px calc(28px + var(--toggle-band, 0px))',
  touchAction: 'pan-y',
});

/**
 * The folder tab. The panel's own paper, clipped to a tab that leaves the panel's
 * edge on an S-curve, runs straight, and curves back in.
 */
const Tab = styled('button')({
  position: 'absolute',
  left: 'calc(100% - 1px)',
  /**
   * 77.5% of the height, capped so the tab's foot always clears the view
   * toggle's band. They touch at about 700px of height and the 12px clearance
   * fails below about 753; on a phone this keeps the two apart at 320x568. With
   * no band (every layout but a phone's) the cap never binds.
   */
  top: `min(${TAB_AT}, calc(100% - var(--toggle-band, 0px) - ${TAB_H / 2}px))`,
  width: TAB_W + 1,
  height: TAB_H,
  transform: 'translateY(-50%)',
  appearance: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  cursor: 'pointer',
  display: 'grid',
  placeItems: 'center',
  color: BRAND.parchment,
  ...PAPER_TEXTURE,
  clipPath: `path('M0 0 C0 ${CURVE_H * 0.55} ${TAB_W + 1} ${CURVE_H * 0.45} ${TAB_W + 1} ${CURVE_H} L${TAB_W + 1} ${
    TAB_H - CURVE_H
  } C${TAB_W + 1} ${TAB_H - CURVE_H * 0.45} 0 ${TAB_H - CURVE_H * 0.55} 0 ${TAB_H} Z')`,
  touchAction: 'pan-y',
  WebkitTapHighlightColor: 'transparent',
  '&:focus-visible': { outline: 'none' },
  '&:focus-visible > span': { textDecoration: 'underline', textUnderlineOffset: 4 },
});

const TabLabel = styled('span')({
  writingMode: 'vertical-rl',
  fontFamily: FONTS.serif,
  fontWeight: 700,
  fontSize: 17,
  lineHeight: 1,
  whiteSpace: 'nowrap',
  // Optically centred: the glyphs sit toward the baseline side of the line.
  marginLeft: 1,
});

/** The ✕, on the scrim just above the tab, over the same column. */
const Close = styled('button')({
  position: 'absolute',
  left: '100%',
  top: `calc(${TAB_AT} - ${TAB_H / 2 + 17 + 18}px)`,
  width: TAB_W,
  height: 36,
  appearance: 'none',
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  display: 'grid',
  placeItems: 'center',
  color: BRAND.parchment,
  opacity: 0,
  pointerEvents: 'none',
  WebkitTapHighlightColor: 'transparent',
});

export interface FilterPanelHandle {
  /** Follow a swipe right begun on the events list: dx since it began. */
  dragOpen: (dx: number) => void;
  /** That swipe lifted. */
  releaseOpen: (dx: number, velocity: number) => void;
  cancelOpen: () => void;
  /**
   * Explore's offset while the pages move, so the tab travels with its page:
   * px from where Explore rests, and the motion to get there.
   */
  follow: (offset: number, ms: number, ease: string) => void;
}

interface Props {
  ref?: Ref<FilterPanelHandle>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Explore is the page showing. Off it, the tab is off screen and can't be reached or opened. */
  available: boolean;
  shown: number;
  /** Every listing; no longer drawn (the panel says "N events"), kept for the caller. */
  total: number;
  /** Swipes on the panel and the scrim. */
  swipes: boolean;
  /** Mouse drags count as swipes: on a phone, which is to say the phone emulator. */
  mouseSwipes: boolean;
  children: ReactNode;
}

export default function FilterPanel({
  ref,
  open,
  onOpenChange,
  available,
  shown,
  swipes,
  mouseSwipes,
  children,
}: Props) {
  const panel = useRef<HTMLDivElement | null>(null);
  const scrim = useRef<HTMLDivElement | null>(null);
  const close = useRef<HTMLButtonElement | null>(null);
  const tab = useRef<HTMLButtonElement | null>(null);
  const contents = useRef<HTMLDivElement | null>(null);

  /** 0 closed, 1 open. */
  const progress = useRef(open ? 1 : 0);
  /** Where Explore is, px from rest. */
  const offset = useRef(0);
  /** Set when a release has already animated to the state `open` is about to become. */
  const settled = useRef(false);
  const openNow = useRef(open);
  openNow.current = open;
  const availableNow = useRef(available);
  availableNow.current = available;

  const paint = useCallback((p: number, ms = 0, ease = EASE_IN_OUT) => {
    progress.current = p;
    const motion = `${ms}ms ${ease}`;
    const panelEl = panel.current;
    const scrimEl = scrim.current;
    const closeEl = close.current;
    if (panelEl) {
      panelEl.style.transition = `transform ${motion}`;
      panelEl.style.transform = `translateX(calc(${((p - 1) * 100).toFixed(3)}% + ${offset.current}px))`;
    }
    if (scrimEl) {
      // Visible from the first frame of opening; hidden only once closing has finished.
      scrimEl.style.transition = `opacity ${motion}, visibility 0ms linear ${p > 0 ? 0 : ms}ms`;
      scrimEl.style.opacity = String(p);
      scrimEl.style.visibility = p > 0 ? 'visible' : 'hidden';
      scrimEl.style.pointerEvents = p > 0 ? 'auto' : 'none';
    }
    if (closeEl) {
      closeEl.style.transition = `opacity ${motion}`;
      closeEl.style.opacity = String(p);
      closeEl.style.pointerEvents = p > 0.5 ? 'auto' : 'none';
    }
  }, []);

  const width = () => panel.current?.offsetWidth || window.innerWidth;

  /** Finish from wherever a drag left the panel. */
  const settle = useCallback(
    (to: 0 | 1) => {
      const ms = Math.max(160, Math.round(OPEN_MS * Math.abs(to - progress.current)));
      paint(to, ms, EASE_OUT);
      if ((to === 1) !== openNow.current) {
        settled.current = true;
        onOpenChange(to === 1);
      }
    },
    [paint, onOpenChange],
  );

  /**
   * A tap on the tab, the ✕ or the scrim, or Escape: the animation starts in this
   * frame, before the state change renders the page behind it.
   */
  const animateTo = useCallback(
    (next: boolean) => {
      if (next === openNow.current || (next && !availableNow.current)) return;
      paint(next ? 1 : 0, OPEN_MS, EASE_IN_OUT);
      settled.current = true;
      onOpenChange(next);
    },
    [paint, onOpenChange],
  );

  // Opened or closed from outside (a change of page closes it), or first drawn.
  const first = useRef(true);
  useLayoutEffect(() => {
    if (first.current) {
      first.current = false;
      paint(open ? 1 : 0);
      return;
    }
    if (settled.current) {
      settled.current = false;
      return;
    }
    paint(open ? 1 : 0, OPEN_MS, EASE_IN_OUT);
  }, [open, paint]);

  // Into the panel on open, for keyboards; back to the tab on close.
  useEffect(() => {
    if (open) {
      contents.current?.focus({ preventScroll: true });
      const onKey = (e: KeyboardEvent) => {
        // Not while the account page is over the app: Escape is that page's.
        if (e.key === 'Escape') animateTo(false);
      };
      window.addEventListener('keydown', onKey);
      return () => window.removeEventListener('keydown', onKey);
    }
    if (contents.current?.contains(document.activeElement)) tab.current?.focus({ preventScroll: true });
  }, [open, animateTo]);

  useImperativeHandle(
    ref,
    () => ({
      dragOpen: (dx) => paint(Math.min(1, Math.max(0, dx / width()))),
      releaseOpen: (dx, velocity) => settle(commits(dx, velocity, width(), 1) ? 1 : 0),
      cancelOpen: () => settle(0),
      follow: (px, ms, ease) => {
        offset.current = px;
        paint(progress.current, ms, ease);
      },
    }),
    [paint, settle],
  );

  // A swipe left on the open panel or the scrim pushes it closed; a swipe right
  // on the closed tab pulls it open.
  const accepts = (e: React.PointerEvent) =>
    available && swipes && (e.pointerType !== 'mouse' || mouseSwipes) && !startsOnOwnDrag(e.target);

  const onPanelSwipe = useSwipeGesture({
    accepts,
    claim: (direction) => (openNow.current ? direction === -1 : direction === 1),
    move: (dx) => {
      if (openNow.current) paint(Math.min(1, Math.max(0, 1 + dx / width())));
      else paint(Math.min(1, Math.max(0, dx / width())));
    },
    release: (dx, velocity) => {
      if (openNow.current) settle(commits(dx, velocity, width(), -1) ? 0 : 1);
      else settle(commits(dx, velocity, width(), 1) ? 1 : 0);
    },
    cancel: () => settle(openNow.current ? 1 : 0),
  });

  const onScrimSwipe = useSwipeGesture({
    accepts,
    claim: (direction) => direction === -1,
    move: (dx) => paint(Math.min(1, Math.max(0, 1 + dx / width()))),
    release: (dx, velocity) => settle(commits(dx, velocity, width(), -1) ? 0 : 1),
    cancel: () => settle(1),
  });

  return (
    <>
      <Scrim ref={scrim} aria-hidden="true" onPointerDown={onScrimSwipe} onClick={() => animateTo(false)} />

      <Panel ref={panel} onPointerDown={onPanelSwipe}>
        <Contents
          ref={contents}
          id="filter-panel"
          role="dialog"
          aria-modal={open || undefined}
          aria-label="Search and filter"
          tabIndex={-1}
          inert={!open}
        >
          <Bar>
            <Title>Search and Filter</Title>
            <Count>
              {shown} {shown === 1 ? 'event' : 'events'}
            </Count>
          </Bar>
          <Body>{children}</Body>
        </Contents>

        <Close ref={close} type="button" aria-label="Close search and filter" tabIndex={open ? 0 : -1} onClick={() => animateTo(false)}>
          <svg width={15} height={15} viewBox="0 0 15 15" aria-hidden="true" focusable="false">
            <path d="M1.5 1.5l12 12M13.5 1.5l-12 12" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
          </svg>
        </Close>

        <Tab
          ref={tab}
          type="button"
          aria-expanded={open}
          aria-controls="filter-panel"
          aria-hidden={!available || undefined}
          tabIndex={available ? 0 : -1}
          aria-label={open ? 'Close search and filter' : 'Open search and filter'}
          onClick={() => animateTo(!openNow.current)}
        >
          <TabLabel aria-hidden="true">Search + Filter</TabLabel>
        </Tab>
      </Panel>
    </>
  );
}
